/**
 * Which profile fields the signed-in user may change on a person, mirroring
 * PUT /api/users/:uid so that no save ends in a 403 (C-174, C-175):
 * - the tenant owner changes every field of everyone;
 * - nobody else changes anything on a tenant owner;
 * - on their own profile, a non-owner changes only their phone, plus their
 *   name when they manage a hotel or a function. Contract, birth date,
 *   skills and notes are set by the owner or by a manager of their hotel.
 * Fields the user may not change stay visible, read-only.
 */
export type ProfileField =
  "displayName" | "phone" | "birthDate" | "employment" | "notes" | "skills";

export type ProfileReadOnlyReason = "owner_target" | "self";

export interface ProfileEditScope {
  canEdit(field: ProfileField): boolean;
  /** Why some fields are read-only, or null when every field is editable. */
  readonly reason: ProfileReadOnlyReason | null;
}

export const MANAGER_ROLES: ReadonlySet<string> = new Set(["manager_hotel", "manager_functional"]);

export function profileEditScope(input: {
  readonly viewerIsOwner: boolean;
  readonly isSelf: boolean;
  /** The person's memberships; on a self-edit they are the viewer's own. */
  readonly memberships: ReadonlyArray<{ readonly role: string }>;
}): ProfileEditScope {
  if (input.viewerIsOwner) return { canEdit: () => true, reason: null };
  if (input.isSelf) {
    const isManager = input.memberships.some((m) => MANAGER_ROLES.has(m.role));
    const editable: ReadonlySet<ProfileField> = new Set<ProfileField>(
      isManager ? ["displayName", "phone"] : ["phone"],
    );
    return { canEdit: (field) => editable.has(field), reason: "self" };
  }
  if (input.memberships.some((m) => m.role === "tenant_owner")) {
    return { canEdit: () => false, reason: "owner_target" };
  }
  return { canEdit: () => true, reason: null };
}
